import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { ApiError, api, type PublicAlbumRow } from '@/lib/api';
import AllianceLogo from '@/components/AllianceLogo';

export default function PublicAlbum() {
  const { slug } = useParams();
  const summaryQuery = useQuery({
    queryKey: ['public-album-summary', slug],
    queryFn: () => api.getPublicAlbumSummary(slug!),
    enabled: !!slug,
    retry: false,
  });
  const alliance = summaryQuery.data?.alliance;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="border-b border-slate-800 bg-slate-900/60">
        <div className="mx-auto flex max-w-5xl items-center gap-4 px-4 py-4">
          <AllianceLogo alliance={alliance} className="h-14" />
          <div>
            <div className="text-lg font-semibold">
              {alliance ? `${alliance.tag || alliance.name} — Alliance Album` : 'Alliance Album'}
            </div>
            {(alliance?.motto || alliance?.tag) && (
              <div className="text-xs text-slate-400">{alliance.motto || alliance.name}</div>
            )}
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-6">
        {summaryQuery.isLoading && (
          <div className="text-slate-400">Loading…</div>
        )}
        {summaryQuery.isError && (
          <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-6 text-sm text-red-300">
            Album not found or not published.
          </div>
        )}
        {summaryQuery.data && (
          <PublicAlbumBody
            slug={slug!}
            summary={summaryQuery.data}
          />
        )}
      </main>
    </div>
  );
}

function PublicAlbumBody({
  slug,
  summary,
}: {
  slug: string;
  summary: { title: string; description: string | null; requiresPassword: boolean; hasAccess: boolean };
}) {
  if (summary.requiresPassword && !summary.hasAccess) {
    return <PasswordGate slug={slug} title={summary.title} />;
  }
  return <AlbumGallery slug={slug} title={summary.title} description={summary.description} />;
}

function PasswordGate({ slug, title }: { slug: string; title: string }) {
  const qc = useQueryClient();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const verify = useMutation({
    mutationFn: () => api.verifyPublicAlbum(slug, password),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['public-album-summary', slug] });
      setError(null);
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Wrong password'),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!password) return;
        verify.mutate();
      }}
      className="mx-auto mt-8 max-w-md space-y-4 rounded-lg border border-slate-800 bg-slate-900/40 p-6"
    >
      <div>
        <h1 className="text-xl font-semibold">{title}</h1>
        <p className="mt-1 text-sm text-slate-400">
          This album is password-protected. Please enter the shared password to view it.
        </p>
      </div>
      <div>
        <label className="mb-1 block text-xs uppercase tracking-wider text-slate-400">
          Password
        </label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          className="w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
        />
      </div>
      {error && <div className="text-sm text-red-400">{error}</div>}
      <button
        type="submit"
        disabled={verify.isPending || !password}
        className="w-full rounded bg-indigo-600 px-4 py-2 text-sm font-medium hover:bg-indigo-500 disabled:opacity-50"
      >
        {verify.isPending ? 'Verifying…' : 'View album'}
      </button>
    </form>
  );
}

function AlbumGallery({
  slug,
  title,
  description,
}: {
  slug: string;
  title: string;
  description: string | null;
}) {
  const detailQuery = useQuery({
    queryKey: ['public-album', slug],
    queryFn: () => api.getPublicAlbum(slug),
  });
  const [slideIndex, setSlideIndex] = useState<number | null>(null);

  if (detailQuery.isLoading) return <div className="text-slate-400">Loading gallery…</div>;
  if (detailQuery.isError || !detailQuery.data)
    return <div className="text-red-400">Failed to load album.</div>;

  // Server already sorts by sortOrder ASC; keep that order.
  const rows = detailQuery.data.rows;

  return (
    <div className="space-y-6">
      <section className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{title}</h1>
          {description && (
            <p className="mt-2 whitespace-pre-wrap text-sm text-slate-300">{description}</p>
          )}
        </div>
        {rows.length > 0 && (
          <button
            type="button"
            onClick={() => setSlideIndex(0)}
            className="rounded bg-indigo-600 px-4 py-2 text-sm font-medium hover:bg-indigo-500"
          >
            Flip through
          </button>
        )}
      </section>

      {rows.length === 0 ? (
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-6 text-sm text-slate-400">
          No photos in this album yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((row, i) => (
            <button
              key={row.entryId}
              type="button"
              onClick={() => setSlideIndex(i)}
              className="group overflow-hidden rounded-lg border border-slate-800 bg-slate-900/40 text-left transition hover:border-indigo-500 hover:bg-slate-900"
            >
              <div className="aspect-[3/4] bg-slate-950">
                <img
                  src={api.publicAlbumPhotoUrlByEntry(slug, row.entryId, row.updatedAt)}
                  alt={row.name}
                  className="h-full w-full object-cover transition group-hover:scale-[1.02]"
                  style={{
                    objectPosition: `${row.focusX * 100}% ${row.focusY * 100}%`,
                  }}
                />
              </div>
              <div className="p-3">
                <div className="text-base font-medium">{row.name}</div>
                {row.realName && (
                  <div className="text-sm text-slate-300">{row.realName}</div>
                )}
                {row.bio && (
                  <p className="mt-2 whitespace-pre-wrap text-sm text-slate-300">
                    {row.bio}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {slideIndex !== null && (
        <Lightbox
          slug={slug}
          rows={rows}
          index={slideIndex}
          onIndex={setSlideIndex}
          onClose={() => setSlideIndex(null)}
        />
      )}
    </div>
  );
}

function Lightbox({
  slug,
  rows,
  index,
  onIndex,
  onClose,
}: {
  slug: string;
  rows: PublicAlbumRow[];
  index: number;
  onIndex: (i: number | null) => void;
  onClose: () => void;
}) {
  const total = rows.length;
  const clamp = useCallback(
    (i: number) => Math.max(0, Math.min(total - 1, i)),
    [total],
  );
  const goto = useCallback(
    (i: number) => onIndex(clamp(i)),
    [clamp, onIndex],
  );
  const prev = useCallback(() => goto(index - 1), [goto, index]);
  const next = useCallback(() => goto(index + 1), [goto, index]);

  // Keyboard nav.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === ' ') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        prev();
      } else if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'Home') {
        goto(0);
      } else if (e.key === 'End') {
        goto(total - 1);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, goto, onClose, total]);

  // Preload neighbors.
  useEffect(() => {
    const preload = (i: number) => {
      if (i < 0 || i >= total) return;
      const img = new Image();
      img.src = api.publicAlbumPhotoUrlByEntry(slug, rows[i].entryId, rows[i].updatedAt);
    };
    preload(index + 1);
    preload(index - 1);
  }, [index, rows, slug, total]);

  // Touch swipe.
  const touchStart = useRef<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    touchStart.current = e.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStart.current == null) return;
    const end = e.changedTouches[0]?.clientX ?? touchStart.current;
    const dx = end - touchStart.current;
    touchStart.current = null;
    if (Math.abs(dx) < 40) return;
    if (dx < 0) next();
    else prev();
  };

  const row = rows[index];
  if (!row) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-slate-950/95 backdrop-blur"
      role="dialog"
      aria-modal="true"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="flex items-center justify-between border-b border-slate-800 px-4 py-2 text-sm text-slate-300">
        <span className="tabular-nums">
          {index + 1} / {total}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-100"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>

      <div className="relative flex flex-1 items-center justify-center overflow-hidden">
        <button
          type="button"
          onClick={prev}
          disabled={index === 0}
          className="absolute left-2 z-10 rounded-full bg-slate-900/70 p-3 text-slate-100 hover:bg-slate-800 disabled:opacity-30 sm:left-4"
          aria-label="Previous"
        >
          <ChevronLeft size={24} />
        </button>

        <article className="mx-2 flex h-full w-full max-w-5xl flex-col items-stretch gap-4 p-2 sm:mx-14 sm:p-4 sm:flex-row">
          <div className="flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden rounded-lg bg-slate-950 sm:max-w-none sm:flex-[3]">
            <img
              key={row.entryId}
              src={api.publicAlbumPhotoUrlByEntry(slug, row.entryId, row.updatedAt)}
              alt={row.name}
              className="h-full w-full object-contain"
              style={{
                objectPosition: `${row.focusX * 100}% ${row.focusY * 100}%`,
              }}
            />
          </div>
          <div className="flex w-full shrink-0 flex-col justify-center gap-3 sm:flex-[2] sm:shrink">
            <div>
              <div className="text-2xl font-semibold">{row.name}</div>
              {row.realName && (
                <div className="mt-1 text-base text-slate-300">{row.realName}</div>
              )}
            </div>
            {row.bio && (
              <p className="whitespace-pre-wrap text-base leading-relaxed text-slate-200">
                {row.bio}
              </p>
            )}
          </div>
        </article>

        <button
          type="button"
          onClick={next}
          disabled={index === total - 1}
          className="absolute right-2 z-10 rounded-full bg-slate-900/70 p-3 text-slate-100 hover:bg-slate-800 disabled:opacity-30 sm:right-4"
          aria-label="Next"
        >
          <ChevronRight size={24} />
        </button>
      </div>

      <div className="border-t border-slate-800 bg-slate-950/80 px-4 py-2 text-center text-xs text-slate-500">
        Use ← → to navigate · Esc to close
      </div>
    </div>
  );
}
